/* eslint-disable react/prop-types */
/* eslint-disable react/function-component-definition */

// Material Dashboard 2 React components
import { useState } from "react"
import { Card, CircularProgress, Icon, Modal, Tooltip } from "@mui/material"
import PropTypes from "prop-types"
import moment from "moment/moment"
import { useNavigate } from "react-router-dom"

import MDBox from "components/MDBox"
import MDTypography from "components/MDTypography"
import MDBadge from "components/MDBadge"
import MDButton from "components/MDButton"
import MDSnackbar from "components/MDSnackbar"
import MDCheckbox from "components/MDCheckbox"
import { translateStatus } from "utils/translate/status"

// Import services
import * as packageService from "services/packages/package.service"

// Document
import { generateColorPerStatus } from "utils/functions/switchStatusColor"

const StateCity = ({ state, city }) => (
  <MDBox lineHeight={1} textAlign="left">
    <MDTypography display="block" variant="p" color="text" fontWeight="medium">
      {state}
    </MDTypography>
    <MDTypography variant="caption">{city}</MDTypography>
  </MDBox>
)

const Action = ({ packageId, companyName, packageStatus }) => {
  const navigate = useNavigate()
  const [isDeleteModalOpened, setIsDeleteModalOpened] = useState(false)
  const [isConfirmModalOpened, setIsConfirmModalOpened] = useState(false)
  const [snackbarObject, setSnackbarObject] = useState({
    open: false,
    content: "",
    color: "primary",
    icon: "",
  })
  const [isConfirming, setIsConfirming] = useState(false)
  const [isCancelling, setIsCancelling] = useState(false)

  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 400,
    boxShadow: 24,
    p: 4,
  }

  const handleOpenDeleteModal = () => setIsDeleteModalOpened(true)
  const handleCloseDeleteModal = () => setIsDeleteModalOpened(false)

  const handleOpenConfirmModal = () => setIsConfirmModalOpened(true)
  const handleCloseConfirmModal = () => setIsConfirmModalOpened(false)

  const handleOpenSnackbar = (status, content, icon) => {
    setSnackbarObject({
      ...snackbarObject,
      open: true,
      content,
      color: status,
      icon,
    })
  }

  const handleCloseSnackbar = () => {
    setSnackbarObject({ ...snackbarObject, open: false })
  }

  const updatePackage = () => {
    navigate(`/pre-selection-commandes/modifier-commande/${packageId}`)
  }

  const removePackage = async () => {
    setIsCancelling(true)
    try {
      const { status } = await packageService.cancelUnconfirmedPackage(packageId)
      if (status === 200) {
        handleCloseDeleteModal()
        handleOpenSnackbar("success", "Colis annulé avec succès", "check_circle")
        navigate(0)
      }
    } catch (error) {
      handleOpenSnackbar("error", "Une erreur est survenue", "error")
    } finally {
      setIsCancelling(false)
    }
  }

  const handleViewDetails = () => {
    navigate(`/pre-selection-commandes/details-commande/${packageId}`)
  }

  const handleConfirmSinglePackage = async () => {
    setIsConfirming(true)

    try {
      const response = await packageService.confirmPackage(packageId)
      if (response.status === 200) {
        handleOpenSnackbar("success", "Colis confirmé avec succès", "check_circle")
        setTimeout(() => navigate(0), 1500)
      } else {
        const message = response?.data?.message || "Impossible de confirmer le colis"
        handleOpenSnackbar("error", message, "error")
      }
    } catch (error) {
      handleOpenSnackbar("error", "Impossible de confirmer le colis", "error")
    } finally {
      handleCloseConfirmModal()
      setIsConfirming(false)
    }
  }

  // const handleConfirmMultiplePackages = async (packageIds = []) => {
  //   try {
  //     const response = await packageService.confirmMultiplePackages(packageIds)
  //     if (response.status === 200) {
  //       handleOpenSnackbar("success", `${packageIds.length} colis confirmés`, "check_circle")
  //       // navigate(0)
  //     }
  //   } catch (error) {
  //     handleOpenSnackbar("error", "Impossible de confirmer les colis", "error")
  //   }
  // }

  return (
    <>
      <MDBox lineHeight={1} display="flex" textAlign="left" gap={1}>
        {/* Voir détails */}
        <Tooltip title="Voir détails">
          <MDButton size="small" variant="outlined" color="secondary" onClick={handleViewDetails} iconOnly>
            <Icon>visibility</Icon>
          </MDButton>
        </Tooltip>

        {/* Edit */}
        <Tooltip title="Modifier">
          <MDButton
            size="small"
            variant="outlined"
            color="info"
            onClick={updatePackage}
            iconOnly
            disabled={packageStatus?.toLowerCase() === "non confirmé"}
          >
            <Icon>edit</Icon>
          </MDButton>
        </Tooltip>

        {/* Confirmer le colis */}
        <Tooltip title="Confirmer">
          <MDButton
            size="small"
            variant="outlined"
            color="success"
            onClick={handleOpenConfirmModal}
            iconOnly
            disabled={packageStatus?.toLowerCase() === "non confirmé" || isConfirming}
          >
            <Icon>check</Icon>
          </MDButton>
        </Tooltip>

        {/* Supprimer / Annuler */}
        <Tooltip title="Annuler">
          <MDButton
            size="small"
            variant="outlined"
            color="error"
            onClick={handleOpenDeleteModal}
            iconOnly
            disabled={packageStatus?.toLowerCase() === "non confirmé"}
          >
            <Icon>cancel</Icon>
          </MDButton>
        </Tooltip>
      </MDBox>

      {/* Annuler Modal */}
      <Modal open={isDeleteModalOpened} onClose={handleCloseDeleteModal}>
        <Card sx={style}>
          <MDTypography variant="h6" component="h2">
            Annuler le colis
          </MDTypography>
          <MDTypography sx={{ mt: 2 }} variant="body2">
            Voulez-vous vraiment annuler cette commande ? Cette action ne pourra pas être annulée.
          </MDTypography>
          <MDBox lineHeight={1} display="flex" justifyContent="flex-end" mt={2} gap={1}>
            <MDButton
              size="small"
              variant="outlined"
              color="secondary"
              onClick={handleCloseDeleteModal}
              disabled={isCancelling}
            >
              Fermer
            </MDButton>
            <MDButton size="small" variant="outlined" color="error" onClick={removePackage} disabled={isCancelling}>
              {isCancelling ? (
                <>
                  <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                  Annulation...
                </>
              ) : (
                "Confirmer"
              )}
            </MDButton>
          </MDBox>
        </Card>
      </Modal>

      {/* Confirm Modal */}

      <Modal open={isConfirmModalOpened} onClose={isConfirming ? undefined : handleCloseConfirmModal}>
        <Card sx={style}>
          {isConfirming ? (
            <MDBox
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <MDTypography component="p" variant="body2">
                Loading...
              </MDTypography>
            </MDBox>
          ) : (
            <>
              <MDTypography variant="h6" component="h2">
                Confirmer la commande
              </MDTypography>
              <MDTypography sx={{ mt: 2 }} variant="body2">
                Êtes-vous sûr de vouloir confirmer cette commande ?
              </MDTypography>
              <MDBox lineHeight={1} display="flex" justifyContent="flex-end" mt={2} gap={1}>
                <MDButton
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={handleCloseConfirmModal}
                  disabled={isConfirming}
                >
                  Annuler
                </MDButton>
                <MDButton
                  size="small"
                  variant="outlined"
                  color="success"
                  onClick={handleConfirmSinglePackage}
                  disabled={isConfirming}
                >
                  {isConfirming ? (
                    <>
                      <CircularProgress size={18} color="inherit" sx={{ mr: 1 }} />
                      Confirmation...
                    </>
                  ) : (
                    "Confirmer"
                  )}
                </MDButton>
              </MDBox>
            </>
          )}
        </Card>
      </Modal>

      <MDSnackbar
        color={snackbarObject.color}
        icon={snackbarObject.icon}
        title="Colis"
        content={snackbarObject.content}
        open={snackbarObject.open}
        close={handleCloseSnackbar}
        dateTime=""
        sx={{ float: "left" }}
      />
    </>
  )
}

// Get all package list from the server
const packageRows = (packageList) => {
  const localPackageList = []
  if (packageList.length === 0) return localPackageList
  for (let packageIndex = 0; packageIndex < packageList.length; packageIndex++) {
    const packageItem = packageList[packageIndex]

    localPackageList.push({
      package: (
        <MDTypography
          component="a"
          href={`pre-selection-commandes/details-commande/${packageItem.packageId}`}
          variant="p"
          color="text"
          fontWeight="medium"
        >
          #{packageItem.reference}
        </MDTypography>
      ),
      barcode: (
        <MDTypography
          component="a"
          href={`pre-selection-commandes/details-commande/${packageItem.packageId}`}
          variant="p"
          color="text"
          fontWeight="medium"
        >
          {packageItem.barcode}
        </MDTypography>
      ),
      company: (
        <MDTypography
          component="a"
          href={`pre-selection-commandes/details-commande/${packageItem.packageId}`}
          variant="p"
          color="text"
          fontWeight="medium"
        >
          {packageItem.companyName}
        </MDTypography>
      ),
      client: (
        <MDTypography
          component="a"
          href={`pre-selection-commandes/details-commande/${packageItem.packageId}`}
          variant="p"
          color="text"
          fontWeight="medium"
        >
          {packageItem.clientName}
        </MDTypography>
      ),
      state: <StateCity state={packageItem.state} city={packageItem.city} />,
      clientPhoneNumber: (
        <MDTypography
          component="a"
          href={`pre-selection-commandes/details-commande/${packageItem.packageId}`}
          variant="p"
          color="text"
          fontWeight="medium"
        >
          {packageItem.clientPhoneNumber}
        </MDTypography>
      ),
      price: (
        <MDTypography
          component="a"
          href={`pre-selection-commandes/details-commande/${packageItem.packageId}`}
          variant="p"
          color="text"
          fontWeight="medium"
        >
          {packageItem.packagePrice}
        </MDTypography>
      ),
      status: (
        <MDBox ml={-1}>
          <MDBadge
            badgeContent={translateStatus(packageItem.status)}
            sx={{
              ".MuiBadge-badge": {
                background: generateColorPerStatus(packageItem.status),
              },
            }}
            variant="gradient"
            size="md"
          />
        </MDBox>
      ),
      date: (
        <MDTypography
          component="a"
          href={`pre-selection-commandes/details-commande/${packageItem.packageId}`}
          variant="p"
          color="text"
          fontWeight="medium"
        >
          {moment(packageItem.createdAt).format("MMM DD, YYYY")}
        </MDTypography>
      ),
      actions: (
        <Action
          packageId={packageItem.packageId}
          companyName={packageItem.companyName}
          packageStatus={packageItem?.status}
        />
      ),
    })
  }
  return localPackageList
}

export default function PackagesPreselectionTable(packageList = []) {
  return {
    columns: [
      {
        id: "selection",
        Header: ({ getToggleAllRowsSelectedProps }) => <MDCheckbox {...getToggleAllRowsSelectedProps()} />,
        Cell: ({ row }) => <MDCheckbox {...row.getToggleRowSelectedProps()} />,
      },
      { Header: "colis (ref)", accessor: "package", width: "10%", align: "left" },
      { Header: "code à barre", accessor: "barcode", width: "10%", align: "left" },
      { Header: "entreprise", accessor: "company", align: "left" },
      { Header: "client(e)", accessor: "client", align: "left" },
      { Header: "état/ville", accessor: "state", align: "left" },
      { Header: "téléphone", accessor: "clientPhoneNumber", align: "center" },
      { Header: "prix (tnd)", accessor: "price", align: "center" },
      { Header: "statut", accessor: "status", align: "center" },
      { Header: "date", accessor: "date", align: "center" },
      { Header: "actions", accessor: "actions", align: "center" },
    ],
    rows: packageRows(packageList),
  }
}

// Setting default values for the props of Action
Action.defaultProps = {}

// Typechecking props of the Action
Action.propTypes = {
  packageId: PropTypes.number.isRequired,
}
